import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from "react-router-dom";

// Material UI
import { Button, Grid, Typography, Paper, Divider, IconButton } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Components
import PaymentDetail from './PaymentDetail';
import Spinner from "../../../components/spinner";
import Input from "../../../components/inputs/input";
import ModalSuccess from "../../../components/modal/submit";

// Hooks
import useStyles from "../styles";
import useNotify from "../../../hooks/notify";
import useUser from '../../../hooks/user/useUser';
import useInternalVoucher from "../../../hooks/internalVouchers/useInternalVoucher";
import usePayments from '../../../hooks/business/usePayments';

// Other
import { utils } from '../../../utils/utils';
import { Authenticate } from "../../authentication";
import { cashTypes, cashTypesSelect } from "../../../models/CashRegisterMovementEntity";

const CreateInternalVoucher = () => {
    const { classes } = useStyles();
    const { internalVoucher, loading, setInternalVoucher, CreateInternalVoucher } = useInternalVoucher();
    const [payments, setPaymets] = useState({
        origin: null,
        destination: null
    })
    const { Notify, variants } = useNotify();
    const history = useNavigate();
    const location = useLocation();
    const user = useUser();

    const [submitOpen, setSubmitOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [order, setOrder] = useState();

    const paymentMethods = usePayments(user?.business.id, order?.subChannel)

    useEffect(() => {
        const MapOrder = () => {
            const stateOrder = location.state.order;
            const invoice = stateOrder.vouchers.find(v => v.type === stateOrder.invoiceType);

            stateOrder.invoiceNumber = utils.formatVoucher(
                stateOrder.country,
                invoice.type,
                invoice.billingCode,
                invoice.number
            )
            setOrder(stateOrder);
            setInternalVoucher(state => ({
                ...state,
                billingCode: Number(invoice.billingCode),
                orderId: stateOrder.id,
                invoiceNumber: Number(invoice.number),
                firstName: stateOrder?.customer?.firstName,
                lastName: stateOrder?.customer?.lastName,
                documentType: stateOrder?.customer?.documentType,
                documentNumber: stateOrder?.customer?.documentNumber,
                phoneNumber: stateOrder?.customer?.phone,
                payments: stateOrder?.payments
            }))
        }

        if (location.state !== undefined && location.state.order !== undefined)
            MapOrder();
        else
            history(`${location.state?.from?.indexOf('internalVouchers?') > -1
                ? location.state.from
                : "/internalVouchers"}`
            )
    }, [location.state, setInternalVoucher]);

    const goBack = () =>
        history(`${location.state?.from?.indexOf('internalVouchers?') > -1 ? location.state.from : "/internalVouchers"}`);

    const handleChangePayment = (payment, type = 'origin') =>
        setPaymets({ ...payments, [type]: payment })

    const handleInputChange = (event) =>
        setInternalVoucher({
            ...internalVoucher,
            [event.target.name]: event.target.value
        });

    const handleSave = async () => {
        if (internalVoucher.amount <= 0)
            return Notify(`No se puede guardar un comprobante con importe en 0 y/o negativo`, variants.ERROR);

        if (payments?.origin?.cashType === cashTypes.cash && payments?.destination?.cashType === cashTypes.cash) {
            if (payments?.destination?.amount <= 0)
                return Notify(`Debes ingresar un monto válido`, variants.ERROR);

            if (payments?.destination?.conversionRate <= 0)
                return Notify(`Debes ingresar una cotización válida`, variants.ERROR);
        } else {
            payments.destination.amount = payments.origin.amount;
            payments.destination.conversionRate = 1;
        }

        try {
            const response = await Authenticate();
            if (!response.account)
                throw new Error("Error al intentar realizar el logueo");

            setSubmitOpen(true);
            const intOrNull = (value) =>
                value ? parseInt(value) : null;

            const mapPyment = (payment) => ({
                cashType: payment.cashType,
                bankId: intOrNull(payment.bankId),
                planId: intOrNull(payment.planId),
                planRefId: payment.planRefId,
                cardId: intOrNull(payment.cashTypeId),
                cardRefId: intOrNull(payment.cardRefId),
                binNumber: payment.bin,
                lastCardDigits: intOrNull(payment.lastCardDigits),
                installmentId: intOrNull(payment.installmentId),
                cuponNumber: intOrNull(payment.cuponNumber),
                currency: payment.currency,
                amount: parseFloat(payment.amount),
                conversionRate: parseFloat(payment.conversionRate)
            })

            const entity = {
                orderId: order.id,
                userEmail: response.account.username,
                paymentId: payments.origin.id,
                Destination: mapPyment(payments.destination),
                observation: internalVoucher.observation
            };

            const res = await CreateInternalVoucher(entity);

            if (!res)
                Notify(`Se produjo un error al intentar crear el comprobante interno`, variants.ERROR);

            setSuccess(!!res);
            setError(!res)
        } catch (err) {
            console.error(err);
            handleError(err.message);
        }
    }

    const handleError = async (err) => {
        setSubmitOpen(false);
        setSuccess(false);
        setError(false);
        Notify(err, variants.ERROR);
    }

    const validateMaxRow = () =>
        (internalVoucher?.cashTypeOrigin === cashTypes.card && internalVoucher?.cashTypeDestination === cashTypes.card)
            ? 15
            : (internalVoucher?.cashTypeOrigin === cashTypes.card || internalVoucher?.cashTypeDestination === cashTypes.card) ? 10 : 6

    const handlePaymentChange = (payment) =>
        setInternalVoucher({ ...internalVoucher, amount: payment.paidAmount, currency: payment.currency })

    const isPaymentValid = (payment) =>
        payment &&
        payment.cashType &&
        (payment.cashType === cashTypes.cash ||
            (payment.cashType === cashTypes.qr &&
                !!payment.cuponNumber) ||
            (payment.cashType === cashTypes.link &&
                !!payment.cuponNumber) ||
            (payment.cashType === cashTypes.card &&
                !!payment.cuponNumber &&
                !!payment.cashTypeId &&
                !!payment.bin &&
                !!payment.lastCardDigits &&
                !!payment.bankId &&
                !!payment.plan &&
                !!payment.planId &&
                !!payment.cardRefId &&
                !!payment.installmentId &&
                !!payment.planRefId
            ))

    const isInternalVoucherValid = () =>
        internalVoucher.amount > 0 &&
        isPaymentValid(payments.origin) &&
        isPaymentValid(payments.destination) &&
        payments.origin?.state === 'Approved'

    const FormControll = ({ label, value }) =>
        <Grid container alignItems='center' justifyContent='space-between'>
            <Grid size={4}>
                <Typography color='primary' variant='subtitle1' component='p'>
                    <strong>{label}:</strong>
                </Typography>
            </Grid>
            <Grid container justifyContent='flex-end' size={8}>
                <Typography variant='body1' data-testid={label}>{value}</Typography>
            </Grid>
        </Grid>

    const GetOptionsDestination = () => {
        if (!payments.origin)
            return [];

        const optionsNew = paymentMethods.paymentMethodsHook.map(item => ({
            value: item.value === "Posnet"
                ? "Card"
                : item.value,
            label: item.title
        }))

        const options = optionsNew
            .filter(p => p.value !== cashTypes.qr)
            .filter(p => p.value !== cashTypes.check);

        return options;
    }

    return (
        <>
            {loading && <Spinner lockScreen />}
            <ModalSuccess
                open={submitOpen}
                success={success}
                error={error}
                successText="Comprobante interno guardado con éxito"
                onSuccess={() => history(`/internalVouchers`)}
                errorText="No se pudo guardar el comprobante interno"
                onError={handleError}
            />
            <Grid container spacing={1}>
                <Grid size={12}>
                    <Paper className={classes.paper}>
                        <Grid container spacing={1} alignItems='center' alignContent='center'>
                            <Grid>
                                <IconButton color='primary' onClick={goBack} size="large">
                                    <ArrowBackIcon />
                                </IconButton>
                            </Grid>
                            <Grid size="grow">
                                <Typography variant='h5' component='h4'>
                                    Nuevo comprobante interno
                                </Typography>
                            </Grid>
                            <Grid>
                                <Button
                                    variant='outlined'
                                    component={Link}
                                    to={`/orders/detail/${internalVoucher?.orderId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<VisibilityIcon />}
                                >
                                    Ver Venta
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        sm: 8
                    }}
                >
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 12,
                                xl: 6
                            }}
                        >
                            <PaymentDetail
                                title='Origen'
                                payments={internalVoucher?.payments}
                                onChange={(payment) => handleChangePayment(payment, 'origin')}
                                options={cashTypesSelect}
                                onPaymentSelected={handlePaymentChange}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                xl: 6
                            }}
                        >
                            <PaymentDetail
                                title='Destino'
                                onChange={(payment) => handleChangePayment(payment, 'destination')}
                                options={GetOptionsDestination()}
                                originPayment={payments.origin}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        sm: 4
                    }}
                >
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Paper className={classes.paper}>
                                <Grid container spacing={1}>
                                    <Grid size={12}>
                                        <Typography variant='h5'>
                                            Detalle General
                                        </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        <Divider />
                                    </Grid>
                                    <Grid size={12}>
                                        <FormControll label='Nro. de orden' value={order?.refId} />
                                        <FormControll label='Importe de la orden' value={`${order?.currency} ${utils.formatMoney(order?.amount || 0)}`} />
                                        <FormControll label='Nro. de Factura' value={order?.invoiceNumber} />
                                        <FormControll label='Fecha creado' value={order?.created ? utils.formatDate(order.created) : ''} />
                                        <FormControll label='Usuario' value={order?.customer.email} />
                                        {internalVoucher.amount && <FormControll label='Importe Pagado' value={`${internalVoucher.currency} ${utils.formatMoney(internalVoucher.amount)}`} />}
                                    </Grid>
                                    <Grid size={12}>
                                        <Input
                                            fullWidth
                                            name='observation'
                                            label="Observación"
                                            multiline
                                            minRows={3}
                                            maxRows={validateMaxRow()}
                                            onChange={handleInputChange}
                                            value={internalVoucher.observation || ''}
                                            InputProps={{
                                                inputProps: {
                                                    min: 0
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <Button
                                            fullWidth
                                            onClick={handleSave}
                                            color='primary'
                                            variant='contained'
                                            disabled={!isInternalVoucherValid()}
                                        >
                                            Guardar
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
export default CreateInternalVoucher;